import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TrendingUp } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../App';

import { BACKEND_URL } from "../utils/apiBase";
const API = `${BACKEND_URL}/api`;

const ResumeDeepAnalysis = () => {
  const { checkupId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!user) {
      navigate('/login');
      return;
    }

    // Automatically initiate checkout
    initiateCheckout();
  }, [user, checkupId]);

  const initiateCheckout = async () => {
    if (!checkupId) {
      setError('Checkup ID is missing. Please try running your analysis again.');
      setIsProcessing(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      // Attach the anonymous checkup to the current user first so it appears in
      // the dashboard and belongs to the Stripe metadata's user_id.
      try {
        await axios.post(
          `${API}/checkups/${checkupId}/attach`, {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (attachErr) {
        // 200 already_attached / 403 belongs_to_another_user handled below by
        // the checkout endpoint. Log and continue.
        console.warn('Attach on resume warning:', attachErr?.response?.data || attachErr);
      }
      const response = await axios.post(
        `${API}/checkout/create-session`,
        { checkup_id: checkupId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      setError(error.response?.data?.detail || 'Failed to create checkout session. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
            <TrendingUp className="h-8 w-8 text-green-600" />
            <span className="text-2xl font-bold text-gray-900">Finance Vitals</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {isProcessing ? (
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-6"></div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Preparing Your Checkout...</h2>
              <p className="text-gray-600">You'll be redirected to Stripe in a moment.</p>
            </div>
          ) : (
            <div className="text-center">
              <div className="text-red-600 text-5xl mb-4">⚠️</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Unable to Continue</h2>
              <p className="text-gray-700 mb-6">{error}</p>
              <button
                onClick={() => navigate('/free-checkup')}
                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
              >
                Return to Free Checkup
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResumeDeepAnalysis;
